import { TFunction } from 'i18next';
import { ReactNode } from 'react';
import {
  SupplierEmailValidationStatus,
  SupplierFormValues,
} from 'src/types/manage-suppliers/SupplierForm';

export interface OrderEmailValidationUi {
  inputClassName?: string;
  helperText?: ReactNode;
  helperClassName?: string;
  showValidationErrorBorder?: boolean;
}

export interface TransmissionValidationMessage {
  text: ReactNode;
  className: string;
}

export const shouldShowTransmissionApprovalMessage = ({
  mode,
  values,
  emailValidationStatus,
  isOrderEmailReadOnly,
}: Pick<
  GetOrderEmailValidationUiParams,
  'mode' | 'values' | 'emailValidationStatus' | 'isOrderEmailReadOnly'
>) => {
  if (values.transmissionType !== 'email') {
    return false;
  }

  if (mode === 'create') {
    return true;
  }

  return emailValidationStatus === 'none' && !isOrderEmailReadOnly;
};

export const getTransmissionValidationMessage = ({
  mode,
  values,
  emailValidationStatus,
  emailValidationDateTime,
  emailValidationExpired,
  t,
}: Pick<
  GetOrderEmailValidationUiParams,
  | 'mode'
  | 'values'
  | 'emailValidationStatus'
  | 'emailValidationDateTime'
  | 'emailValidationExpired'
  | 't'
>): TransmissionValidationMessage | null => {
  if (mode !== 'edit' || values.transmissionType !== 'email') {
    return null;
  }

  switch (emailValidationStatus) {
    case 'pending':
      return {
        text: t('A Validation request has been sent for approval on {{dateTime}}', {
          dateTime: emailValidationDateTime,
        }),
        className: 'text-validation-pending',
      };
    case 'failed':
      if (emailValidationExpired) {
        return {
          text: t('Validation auto-expired after 3 days.'),
          className: 'text-error',
        };
      }
      return null;
    default:
      return null;
  }
};

interface GetOrderEmailValidationUiParams {
  mode: 'create' | 'edit';
  values: Pick<SupplierFormValues, 'transmissionType'>;
  emailValidationStatus: SupplierEmailValidationStatus;
  emailValidationDateTime?: string;
  emailValidationExpired: boolean;
  isOrderEmailReadOnly: boolean;
  t: TFunction;
}

export const getOrderEmailValidationUi = ({
  mode,
  values,
  emailValidationStatus,
  emailValidationDateTime,
  isOrderEmailReadOnly,
  t,
}: GetOrderEmailValidationUiParams): OrderEmailValidationUi => {
  if (mode === 'create') {
    return {};
  }

  if (values.transmissionType !== 'email') {
    return {};
  }

  if (isOrderEmailReadOnly && emailValidationStatus === 'none') {
    return {};
  }

  switch (emailValidationStatus) {
    case 'pending':
      return {
        inputClassName: '[&_.k-input-inner]:!border-validation-pending',
        helperText: t('Pending Validation'),
        helperClassName: 'text-validation-pending',
      };
    case 'validated':
      return {
        inputClassName: '[&_.k-input-inner]:!border-success',
        helperText: t('Email Validated on {{dateTime}}', { dateTime: emailValidationDateTime }),
        helperClassName: 'text-success',
      };
    case 'failed':
      return {
        inputClassName: 'supplier-form__order-email--validation-failed',
        showValidationErrorBorder: true,
        helperText: t('Validation failed on {{dateTime}}', { dateTime: emailValidationDateTime }),
        helperClassName: 'text-error',
      };
    default:
      return {};
  }
};
